import { useSearchParams } from "react-router-dom";
import { useCallback, useMemo, useEffect, useRef } from "react";

type ParamValue = string | number | boolean | object | null | undefined;

interface ParamConfig<T = any> {
  defaultValue: T;
  parse?: (value: string) => T;
  serialize?: (value: T) => string;
  removeWhenDefault?: boolean;
  persist?: boolean; // Whether to persist this param to localStorage
}

type ParamsConfig = {
  [key: string]: ParamConfig;
};

type InferParamType<C extends ParamConfig> = C["defaultValue"];

type InferParamsType<C extends ParamsConfig> = {
  [K in keyof C]: InferParamType<C[K]>;
};

interface UseQueryParametersOptions {
  storageKey?: string; // Key for localStorage
}

export function useQueryParameters<C extends ParamsConfig>(
  config: C,
  options?: UseQueryParametersOptions
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasInitialized = useRef(false);
  const { storageKey } = options || {};

  // Parse current params from URL
  const params = useMemo(() => {
    const result = {} as InferParamsType<C>;

    for (const [key, paramConfig] of Object.entries(config)) {
      const rawValue = searchParams.get(key);

      if (rawValue === null) {
        // No value in URL, use default
        result[key as keyof C] = paramConfig.defaultValue;
      } else {
        // Parse value from URL
        if (paramConfig.parse) {
          try {
            result[key as keyof C] = paramConfig.parse(rawValue);
          } catch (error) {
            console.warn(`Failed to parse param "${key}":`, error);
            result[key as keyof C] = paramConfig.defaultValue;
          }
        } else {
          // Default parsing based on type of defaultValue
          const defaultType = typeof paramConfig.defaultValue;

          if (defaultType === "number") {
            const parsed = Number(rawValue);
            result[key as keyof C] = isNaN(parsed) ? paramConfig.defaultValue : parsed;
          } else if (defaultType === "boolean") {
            result[key as keyof C] = rawValue === "true";
          } else if (defaultType === "object" && paramConfig.defaultValue !== null) {
            try {
              result[key as keyof C] = JSON.parse(rawValue);
            } catch {
              result[key as keyof C] = paramConfig.defaultValue;
            }
          } else {
            result[key as keyof C] = rawValue as any;
          }
        }
      }
    }

    return result;
  }, [searchParams, config]);

  // Load persisted params from localStorage on mount
  useEffect(() => {
    if (!hasInitialized.current && storageKey) {
      hasInitialized.current = true;

      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          const updates: Partial<InferParamsType<C>> = {};
          let hasUpdates = false;

          // Only restore params that:
          // 1. Have persist: true
          // 2. Don't already have a value in URL
          for (const [key, paramConfig] of Object.entries(config)) {
            if (paramConfig.persist && !searchParams.has(key) && parsed[key] !== undefined) {
              updates[key as keyof C] = parsed[key];
              hasUpdates = true;
            }
          }

          if (hasUpdates) {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);

              for (const [key, value] of Object.entries(updates)) {
                const paramConfig = config[key];
                const isDefault = JSON.stringify(value) === JSON.stringify(paramConfig.defaultValue);

                if (!isDefault || paramConfig.removeWhenDefault === false) {
                  let serialized: string;

                  if (paramConfig.serialize) {
                    serialized = paramConfig.serialize(value);
                  } else {
                    if (typeof value === "object" && value !== null) {
                      serialized = JSON.stringify(value);
                    } else {
                      serialized = String(value);
                    }
                  }

                  next.set(key, serialized);
                }
              }

              return next;
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load persisted params:", error);
      }
    }
  }, [storageKey, config, searchParams, setSearchParams]);

  // Save persisted params to localStorage when they change
  useEffect(() => {
    if (hasInitialized.current && storageKey) {
      const toStore: Record<string, any> = {};
      let hasPersistedParams = false;

      for (const [key, paramConfig] of Object.entries(config)) {
        if (paramConfig.persist) {
          toStore[key] = params[key as keyof typeof params];
          hasPersistedParams = true;
        }
      }

      if (hasPersistedParams) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(toStore));
        } catch (error) {
          console.warn("Failed to save persisted params:", error);
        }
      }
    }
  }, [params, config, storageKey]);

  // Set single param
  const setParam = useCallback(
    <K extends keyof C>(key: K, value: InferParamType<C[K]>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const paramConfig = config[key as string];

        // Check if value is default and should be removed
        const isDefault = JSON.stringify(value) === JSON.stringify(paramConfig.defaultValue);

        if (isDefault && paramConfig.removeWhenDefault !== false) {
          next.delete(key as string);
        } else {
          // Serialize value
          let serialized: string;

          if (paramConfig.serialize) {
            serialized = paramConfig.serialize(value);
          } else {
            // Default serialization
            if (typeof value === "object" && value !== null) {
              serialized = JSON.stringify(value);
            } else {
              serialized = String(value);
            }
          }

          next.set(key as string, serialized);
        }

        return next;
      });
    },
    [config, setSearchParams]
  );

  // Set multiple params at once
  const setParams = useCallback(
    (updates: Partial<InferParamsType<C>>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        for (const [key, value] of Object.entries(updates)) {
          if (!(key in config)) continue;

          const paramConfig = config[key];
          const isDefault = JSON.stringify(value) === JSON.stringify(paramConfig.defaultValue);

          if (isDefault && paramConfig.removeWhenDefault !== false) {
            next.delete(key);
          } else {
            let serialized: string;

            if (paramConfig.serialize) {
              serialized = paramConfig.serialize(value);
            } else {
              if (typeof value === "object" && value !== null) {
                serialized = JSON.stringify(value);
              } else {
                serialized = String(value);
              }
            }

            next.set(key, serialized);
          }
        }

        return next;
      });
    },
    [config, setSearchParams]
  );

  // Reset params to default values
  const resetParams = useCallback(
    (keys?: Array<keyof C>) => {
      if (!keys) {
        // Reset all params
        setSearchParams(new URLSearchParams());
      } else {
        // Reset specific params
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);

          for (const key of keys) {
            if (config[key as string]?.removeWhenDefault !== false) {
              next.delete(key as string);
            } else {
              const defaultValue = config[key as string].defaultValue;
              const paramConfig = config[key as string];

              let serialized: string;
              if (paramConfig.serialize) {
                serialized = paramConfig.serialize(defaultValue);
              } else {
                if (typeof defaultValue === "object" && defaultValue !== null) {
                  serialized = JSON.stringify(defaultValue);
                } else {
                  serialized = String(defaultValue);
                }
              }

              next.set(key as string, serialized);
            }
          }

          return next;
        });
      }
    },
    [config, setSearchParams]
  );

  // Clear all params from URL
  const clearParams = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Check if param has non-default value
  const hasChanged = useCallback(
    (key: keyof C) => {
      const currentValue = params[key];
      const defaultValue = config[key as string].defaultValue;
      return JSON.stringify(currentValue) !== JSON.stringify(defaultValue);
    },
    [params, config]
  );

  // Check if any param has non-default value
  const isDirty = useMemo(() => {
    return Object.keys(config).some((key) => {
      const currentValue = params[key];
      const defaultValue = config[key].defaultValue;
      return JSON.stringify(currentValue) !== JSON.stringify(defaultValue);
    });
  }, [params, config]);

  return {
    params,
    setParam,
    setParams,
    resetParams,
    clearParams,
    hasChanged,
    isDirty,
  };
}